import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { X, Lock, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";
import { api } from "../../services/api";
import { toast } from "sonner";

export default function AuthModal({ onClose }) {
  const { login, exchangeSSOTicket } = useAuth();
  const [mode, setMode] = useState("direct"); // "direct" by default for real login
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmitDirect = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Por favor, preencha seu e-mail e senha.");
      return;
    }
    setLoading(true);
    const success = await login(email, password);
    setLoading(false);
    if (success) {
      onClose();
    }
  };

  const handleQuickLogin = async (roleType) => {
    setLoading(true);
    try {
      const emailMap = {
        admin: { email: "admin@joycesxy.com", name: "Joyce Admin", role: "admin", user_id: "u_admin_001" },
        vip: { email: "vip@joycesxy.com", name: "Membro VIP", role: "customer", user_id: "u_vip_002" },
        stock: { email: "estoque@joycesxy.com", name: "Gestor de Estoque", role: "catalog_manager", user_id: "u_stock_003" }
      };
      const target = emailMap[roleType] || emailMap.vip;
      
      const res = await api.post("/auth/sso/generate-ticket", target);
      if (res.data.ticket) {
        const ok = await exchangeSSOTicket(res.data.ticket);
        if (ok) {
          onClose();
        }
      }
    } catch (err) {
      toast.error("Ocorreu um erro. Tente novamente em instantes.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
      <div className="relative w-full max-w-md rounded-3xl bg-[#121214] border border-white/10 shadow-2xl p-6 sm:p-8 text-white">
        
        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="auth-modal-close-btn"
          className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white p-1 rounded-xl hover:bg-white/5 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#18181B] border border-white/10 mb-3 text-[#FF8C69]">
            <Lock className="w-6 h-6" />
          </div>
          <h3 className="font-heading text-2xl font-bold text-white">Acesse sua Conta</h3>
          <p className="text-xs text-[#A1A1AA] mt-1">
            Entre para visualizar seus pedidos e finalizar suas compras.
          </p>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-[#18181B] rounded-2xl mb-6 text-xs font-bold">
          <button
            type="button"
            data-testid="auth-tab-sso"
            onClick={() => setMode("sso_sim")}
            className={`py-2 rounded-xl transition-all ${
              mode === "sso_sim"
                ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md"
                : "text-[#A1A1AA] hover:text-white"
            }`}
          >
            Acesso Rápido
          </button>
          <button
            type="button"
            data-testid="auth-tab-direct"
            onClick={() => setMode("direct")}
            className={`py-2 rounded-xl transition-all ${
              mode === "direct"
                ? "bg-[#FF8C69] text-[#0A0A0B] shadow-md"
                : "text-[#A1A1AA] hover:text-white"
            }`}
          >
            E-mail e Senha
          </button>
        </div>

        {mode === "sso_sim" ? (
          <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-[#18181B] border border-white/10 text-xs space-y-3">
              <div className="flex items-center gap-2 text-[#FF8C69] font-bold">
                <Sparkles className="w-4 h-4" />
                <span>Entrar como:</span>
              </div>
              <p className="text-[#A1A1AA]">
                Selecione seu perfil para conectar instantaneamente:
              </p>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <button
                  type="button"
                  data-testid="sso-quick-vip-btn"
                  onClick={() => handleQuickLogin("vip")}
                  disabled={loading}
                  className="py-2.5 px-2 rounded-xl bg-[#121214] hover:bg-[#FF8C69] hover:text-[#0A0A0B] text-[11px] font-bold border border-white/10 text-white transition-all text-center"
                >
                  Membro VIP
                </button>
                <button
                  type="button"
                  data-testid="sso-quick-admin-btn"
                  onClick={() => handleQuickLogin("admin")}
                  disabled={loading}
                  className="py-2.5 px-2 rounded-xl bg-[#121214] hover:bg-[#FF8C69] hover:text-[#0A0A0B] text-[11px] font-bold border border-white/10 text-[#FF8C69] transition-all text-center"
                >
                  Admin
                </button>
                <button
                  type="button"
                  data-testid="sso-quick-stock-btn"
                  onClick={() => handleQuickLogin("stock")}
                  disabled={loading}
                  className="py-2.5 px-2 rounded-xl bg-[#121214] hover:bg-[#FF8C69] hover:text-[#0A0A0B] text-[11px] font-bold border border-white/10 text-white transition-all text-center"
                >
                  Equipe
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmitDirect} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5">
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seuemail@exemplo.com"
                data-testid="auth-email-input"
                className="w-full bg-[#18181B] border border-white/10 rounded-xl py-2.5 px-4 text-xs text-white placeholder-[#A1A1AA]/40 focus:outline-none focus:border-[#FF8C69]"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-[#A1A1AA] mb-1.5">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="auth-password-input"
                className="w-full bg-[#18181B] border border-white/10 rounded-xl py-2.5 px-4 text-xs text-white placeholder-[#A1A1AA]/40 focus:outline-none focus:border-[#FF8C69]"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-direct-submit-btn"
              className="w-full py-3.5 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-xs font-bold text-[#0A0A0B] shadow-lg transition-all flex items-center justify-center gap-2"
            >
              {loading ? "Entrando..." : "Entrar"}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        )}

      </div>
    </div>
  );
}
